function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

function openEnlargeGallery(sourceMediaGalleryEl, startIndex = 0) {
  const dialog = document.getElementById("enlarge-gallery-dialog");
  if (!dialog) return;
  const dialogGallery = dialog.querySelector("media-gallery");
  if (!dialogGallery) return;
  const sourceContainer = sourceMediaGalleryEl.querySelector('.gallery-container');
  if (!sourceContainer) return;
  dialogGallery.setImages(sourceContainer, startIndex, { enlarge: true });
  dialog.showModal();
}

function openEnlargeGalleryFromImages(figureElements, startIndex = 0) {
  const dialog = document.getElementById("enlarge-gallery-dialog");
  if (!dialog) return;
  const dialogGallery = dialog.querySelector("media-gallery");
  if (!dialogGallery) return;

  // Build a synthetic gallery-container from an array of <figure> elements
  const syntheticContainer = document.createElement("div");
  syntheticContainer.className = "gallery-container transition-opacity flex size-full relative group/gallery min-h-0";

  const wrapper = document.createElement("div");
  wrapper.className = "gallery-wrapper relative flex overflow-hidden bg-warm-100 dark:bg-green-800 rounded-lg size-full min-h-0 h-full";

  const scroller = document.createElement("div");
  scroller.id = "GalleryScroller-enlarge-synthetic";
  scroller.className = "gallery-scroller relative size-full flex min-h-0";
  scroller.dataset.activeIndex = String(startIndex);
  scroller.dataset.mediaCount = String(figureElements.length);

  figureElements.forEach((fig, i) => {
    const item = document.createElement("div");
    item.id = `GalleryItem-enlarge-synthetic-${i}`;
    item.className = `gallery-item absolute flex inset-0 size-full transition-opacity duration-300 justify-center items-center ${i === startIndex ? "opacity-100" : "opacity-0 pointer-events-none"}`;
    item.dataset.index = String(i);
    item.setAttribute("role", "tabpanel");
    item.setAttribute("aria-label", `Gallery item ${i + 1}`);
    item.setAttribute("aria-hidden", i === startIndex ? "false" : "true");
    item.appendChild(fig.cloneNode(true));
    scroller.appendChild(item);
  });

  wrapper.appendChild(scroller);
  syntheticContainer.appendChild(wrapper);

  dialogGallery.setImages(syntheticContainer, startIndex, { enlarge: true });
  dialog.showModal();
}

class HTMLUpdateUtility {
  static #vtQueue = Promise.resolve();

  /**
   * Batch a group of DOM changes into a single View Transition.
   * Elements are paired by a stable key so they morph instead of cross-fading.
   *
   * Usage:
   * HTMLUpdateUtility.batchViewTransition(async () => {
   *   // Do all DOM writes here (replace, insert, reorder, etc.)
   *   // Make sure the *new* elements carry the same key (data-vt-key / id) as the old ones.
   * });
   *
   * @param {() => void|Promise<void>} updateFn - performs all DOM mutations.
   * @param {{
   *   scope?: ParentNode,           // limit pairing to this subtree
   *   selector?: string,            // which nodes to morph
   *   keyAttr?: string,             // attribute holding stable keys
   *   namePrefix?: string,          // prefix for view-transition-name
   *   isolatePaint?: boolean,       // add contain:paint during VT
   *   queue?: boolean               // serialize transitions
   * }} opts
   * @returns {Promise<void>} resolves when the transition finishes (or immediately if unsupported)
   */
  static batchViewTransition(updateFn, opts = {}) {
    const {
      scope = document,
      selector = "[data-vt], [data-vt-key], [vt], [vt-key], [id]",
      keyAttr = "data-vt-key",
      namePrefix = "vt",
      isolatePaint = true,
      queue = true,
    } = opts;

    const run = async () => {
      // Fallback: if VT unsupported, just do the update.
      if (!("startViewTransition" in document)) {
        await updateFn();
        return;
      }

      // 1) Snapshot & tag old elements with names BEFORE the update callback runs.
      const oldNodes = Array.from(scope.querySelectorAll(selector));
      const cleanupOld = [];

      const getKey = (el, i) =>
        el.getAttribute(keyAttr) ||
        el.getAttribute("data-vt") ||
        el.getAttribute("vt-key") ||
        el.getAttribute("vt") ||
        el.id ||
        // last resort: index-based (works if order is stable)
        `idx-${i}`;

      oldNodes.forEach((el, i) => {
        const key = getKey(el, i);
        const vtName = `${namePrefix}-${key}-${i}`;
        el.style.viewTransitionName = vtName;
        if (isolatePaint)
          el.style.contain = `${(el.style.contain || "").trim()} paint`.trim();
        cleanupOld.push(el);
      });

      // 2) Start a single transition and perform ALL DOM writes inside.
      const vt = document.startViewTransition(async () => {
        await updateFn();

        // 3) Tag new elements with matching names AFTER the update.
        const newNodes = Array.from(scope.querySelectorAll(selector));
        const byKey = new Map();
        newNodes.forEach((el, i) => {
          const key =
            el.getAttribute(keyAttr) ||
            el.getAttribute("data-vt") ||
            el.getAttribute("vt-key") ||
            el.getAttribute("vt") ||
            el.id ||
            `idx-${i}`;
          byKey.set(el, `${namePrefix}-${key}-${i}`);
        });

        newNodes.forEach((el) => {
          const vtName = byKey.get(el);
          if (!vtName) return;
          el.style.viewTransitionName = vtName;
          if (isolatePaint)
            el.style.contain = `${(
              el.style.contain || ""
            ).trim()} paint`.trim();
        });
      });

      // 4) Cleanup inline styles once the transition is done.
      try {
        await vt.finished;
      } finally {
        const all = new Set(
          cleanupOld.concat(Array.from(scope.querySelectorAll(selector)))
        );
        all.forEach((el) => {
          // Remove only the bits we added
          if (el.style.viewTransitionName?.startsWith(`${namePrefix}-`)) {
            el.style.viewTransitionName = "";
          }
          if (isolatePaint && el.style.contain) {
            // remove the 'paint' token while keeping any existing others
            const tokens = el.style.contain.split(/\s+/).filter(Boolean);
            const filtered = tokens.filter((t) => t !== "paint");
            el.style.contain = filtered.join(" ");
          }
        });
      }
    };

    // Optional queuing so transitions never overlap
    if (!queue) return run();
    HTMLUpdateUtility.#vtQueue = HTMLUpdateUtility.#vtQueue.then(run, run);
    return HTMLUpdateUtility.#vtQueue;
  }

  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

if (!customElements.get("quantity-input")) {
  customElements.define("quantity-input", QuantityInput);
}

function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/**
 * Format a price in cents with the shop's money format
 * (window.moneyFormat, set from shop.money_format in theme.liquid), so a
 * total computed in JS reads exactly like one rendered by Liquid's `money`.
 * Accepts the placeholders Shopify's own formatter does.
 * @param {number|string} cents
 * @param {string} [format] - e.g. "${{amount}}" (default: window.moneyFormat)
 * @returns {string}
 */
function formatMoney(cents, format = window.moneyFormat || "${{amount}}") {
  const value =
    Number(typeof cents === "string" ? cents.replace(".", "") : cents) || 0;
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const type = (format.match(placeholder) || [])[1] || "amount";

  const withDelimiters = (precision, thousands, decimal) => {
    const [whole, fraction] = (value / 100).toFixed(precision).split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return fraction ? grouped + decimal + fraction : grouped;
  };

  const formats = {
    amount: () => withDelimiters(2, ",", "."),
    amount_no_decimals: () => withDelimiters(0, ",", "."),
    amount_with_comma_separator: () => withDelimiters(2, ".", ","),
    amount_no_decimals_with_comma_separator: () => withDelimiters(0, ".", ","),
    amount_with_space_separator: () => withDelimiters(2, " ", ","),
    amount_no_decimals_with_space_separator: () => withDelimiters(0, " ", ","),
    amount_with_apostrophe_separator: () => withDelimiters(2, "'", "."),
    amount_with_period_and_space_separator: () => withDelimiters(2, " ", "."),
  };

  const formatted = (formats[type] || formats.amount)();
  return format.replace(placeholder, () => formatted);
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

if (!customElements.get("variant-selects")) {
  customElements.define("variant-selects", VariantSelects);
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(metricName, `${metricName}:start`, `${metricName}:end`);
  }
}
